import { createSelector } from '@reduxjs/toolkit';
import { formatQuantity } from '../../utils/inventoryLabels';
import { LOGISTICS_FIELDS } from '../fulfillment/fulfillmentUtils';
import { isContainer, supplyKindLabel } from '../supplies/suppliesUtils';
import { matchCodes, normalizeCode } from '../operator/operatorCodes';
import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../operator/operatorConstants';
import { isTaskOnScreen } from '../operator/operatorTaskScope';
import { asSentence, selectCourierRelabelReady, selectCourierSendReady } from '../operator/operatorUtils';

/*
 * The rules of packing on the floor: what a scanned code is — a box or
 * another supply, or a product of the order — which item it goes to, what
 * the screen showed before each addition, and what to say once the server
 * answered. The API says item; the floor reads «bulto».
 */

/** States where the order has every item weighed and measured. */
export const PACKED_STATES = ['packed', 'labeled'];

export const isOrderPacked = (order) => PACKED_STATES.includes(order?.wms_state);

/** How the floor names an order: its reference, or its number. */
export const packingOrderName = (order) => order?.reference_id || `Orden #${order?.id}`;

/** The supply a code names, by its barcode or SKU; boxes come first in the options. */
export const matchSupply = (order, code) => {
  const wanted = normalizeCode(code);
  if (!wanted) return null;
  return (
    (order?.supply_options || []).find(
      (supply) =>
        (supply.barcode && normalizeCode(supply.barcode) === wanted) || (supply.sku && normalizeCode(supply.sku) === wanted)
    ) || null
  );
};

/**
 * The warehouse's supplies as packing offers them to tap, boxes first as the
 * server sends them: the code a scan of each would read, its measures for a
 * box, and whether none is left to use — the server refuses it then.
 */
export const supplyOptionsOf = (order) =>
  (order?.supply_options || []).map((supply) => ({
    id: supply.id,
    code: supply.barcode || supply.sku,
    sku: supply.sku,
    kind: supply.kind,
    detail:
      isContainer(supply) && supply.length_cm && supply.width_cm && supply.height_cm
        ? `${formatQuantity(supply.length_cm)} × ${formatQuantity(supply.width_cm)} × ${formatQuantity(supply.height_cm)} cm`
        : supply.name,
    out: supply.quantity != null && Number(supply.quantity) < 1,
  }));

/** Which tap a packing read is, so the option tapped shows it is running. */
export const packingTapKey = ({ supplyId, lineId } = {}) => {
  if (supplyId != null) return `supply:${supplyId}`;
  return lineId != null ? `line:${lineId}` : undefined;
};

/** The order lines a product code can be, each with the unit it reads in. */
export const matchPackingLines = (order, code) =>
  (order?.lines || []).reduce((matches, line) => {
    const match = matchCodes(line.codes, code);
    if (match) matches.push({ line, factor: match.factor });
    return matches;
  }, []);

/**
 * Whether a code reads as a product of the order rather than a supply: only a
 * product adds one more each time the camera reads it again. A supply wins, as
 * in the scan, so a box label back in view does not open another item.
 */
export const isPackingProductCode = (order, code) =>
  !matchSupply(order, code) && matchPackingLines(order, code).length > 0;

/**
 * The unit a product code adds, `<product id>:<factor>`, for the scan gate;
 * `null` for a supply, or a code of more than one product.
 */
export const packingUnitOf = (order, code) => {
  if (matchSupply(order, code)) return null;
  const units = new Set(matchPackingLines(order, code).map(({ line, factor }) => `${line.product_id}:${factor}`));
  return units.size === 1 ? [...units][0] : null;
};

/** A line as a choice, when a code is more than one of them. */
export const packingLineOption = ({ line, factor }) => ({
  id: line.line_id,
  factor,
  badge: factor > 1 ? `Caja ×${factor}` : null,
  title: `${line.sku} — ${line.name}`,
  subtitle: `${formatQuantity(line.in_items)} de ${formatQuantity(line.picked)} un en bultos`,
});

/** The item the scans go to: the one chosen, or else the last one. */
export const currentItemOf = (order, currentItemId) => {
  const items = order?.items || [];
  return items.find((item) => item.id === currentItemId) || items[items.length - 1] || null;
};

/** One item of the order, as the screen shows it, or `null`. */
export const itemOf = (order, itemId) => (order?.items || []).find((item) => item.id === itemId) || null;

/** How many items the screen shows: what a new one says it saw. */
export const itemsShown = (order) => (order?.items || []).length;

/**
 * The units of a product the screen shows in an item, over all its lines:
 * what a scan of it says it saw. A row without `product_id` is read by its
 * line. `undefined` for an unknown product, so nothing is said.
 */
export const productInItem = (order, itemId, productId) => {
  if (productId == null) return undefined;
  const productOf = (row) =>
    row.product_id ?? (order?.lines || []).find((line) => line.line_id === row.line_id)?.product_id;
  return (itemOf(order, itemId)?.products || [])
    .filter((row) => productOf(row) === productId)
    .reduce((total, row) => total + Number(row.quantity || 0), 0);
};

/** How much of a supply the screen shows in an item: what using it again says it saw. */
export const supplyInItem = (order, itemId, supplyId) =>
  (itemOf(order, itemId)?.supplies || [])
    .filter((row) => row.supply_id === supplyId)
    .reduce((total, row) => total + Number(row.quantity || 0), 0);

/** What a packing write acts on, which keys its button: the supply use, the item or the order. */
export const packingWriteKey = (arg) => arg?.supplyUseId ?? arg?.itemId ?? arg?.orderId;

/** Whether the server is creating the order's shipment again, after the floor changed its items. */
const isRelabeling = (order) => order?.relabel?.state === 'pending';

/**
 * Whether the order on screen is with its courier: the screen waits for its
 * labels, or the server says an attempt is out — a send, or creating its
 * shipment again. The server takes no packing write of it meanwhile.
 *
 * @param {object} packing - The `operatorPacking` slice.
 */
export const isSendingToCourier = (packing) =>
  Boolean(packing?.courierSend?.labelWait) ||
  packing?.order?.courier_send?.status === 'pending' ||
  isRelabeling(packing?.order);

/**
 * Whether nothing of the order on screen is out: it is loaded, and no write
 * of it is out — even one sent on an earlier visit — or left unconfirmed, nor
 * an addition opening its first item.
 *
 * @param {object} packing - The `operatorPacking` slice.
 */
export const isPackingIdle = (packing) =>
  Boolean(packing?.order) &&
  !packing.loading &&
  !packing.pendingWrite &&
  !packing.opening &&
  !packing.inFlightByOrder?.[packing.orderId] &&
  !packing.unconfirmed?.[packing.orderId];

/**
 * Whether a new packing action may go on the order on screen: one at a time,
 * on the order as the server last showed it (`isPackingIdle`), and none while
 * the order is with its courier. The server tells a retry from a new action
 * by what the screen showed, so anything sent in between would read wrong.
 *
 * @param {object} packing - The `operatorPacking` slice.
 */
export const canSendPacking = (packing) => isPackingIdle(packing) && !isSendingToCourier(packing);

/**
 * What the scan panel says while it takes nothing: a write out is being
 * registered, or one sent on an earlier visit still is; only one left
 * unconfirmed asks to be retried first. Once the order is with its courier,
 * it waits for the labels.
 *
 * @param {object} packing - The `operatorPacking` slice.
 */
export const packingBusyLabel = (packing) => {
  const { orderId, pendingWrite, loading, inFlightByOrder, unconfirmed } = packing;
  if (isSendingToCourier(packing)) {
    return isRelabeling(packing.order) ? 'Esperando la etiqueta nueva…' : 'Esperando la etiqueta del courier…';
  }
  if (pendingWrite) return 'Registrando…';
  if (inFlightByOrder?.[orderId]) return 'Esperando la respuesta anterior…';
  if (loading) return 'Cargando la orden…';
  if (unconfirmed?.[orderId]) return 'Reintenta la última acción antes de seguir';
  return 'Registrando…';
};

/**
 * The write left unconfirmed of the order on screen, `{type, arg}`, or
 * `null`. Packing has no key: it only goes again from that order's screen,
 * where nothing else of the order has gone since.
 *
 * @param {object} state
 */
export const selectPackingUnconfirmed = (state) => {
  const { orderId, unconfirmed } = state.operatorPacking;
  if (orderId === null || !isTaskOnScreen(state, OPERATOR_FLOW.PACKING, orderId)) return null;
  return unconfirmed[orderId] || null;
};

const WHOLE_NUMBER = /^\d+$/;

/** What the save button says of a measure typed wrong: the weight goes in grams, the rest in centimeters. */
const measureProblem = (key, raw) => {
  const weight = key === 'weight_grams';
  if (/^-\s*\d/.test(raw)) return weight ? 'El peso no puede ser negativo' : 'Las medidas no pueden ser negativas';
  return weight ? 'El peso va en gramos enteros' : 'Las medidas van en centímetros enteros';
};

/**
 * The measures the operator changed, as the update takes them: whole numbers,
 * and `null` for one emptied, which clears it. What did not change is left
 * out, and stays; `changed` is `null` when nothing changed. A measure that is
 * not a whole number, or is negative, is never dropped quietly: `problem`
 * says what to fix, and nothing is saved until it is.
 *
 * @param {object} item - As the server has it.
 * @param {Object<string, string>} values - What the form shows.
 * @returns {{changed: object|null, problem: string|null}}
 */
export const changedMeasures = (item, values) => {
  const changed = {};
  let problem = null;
  LOGISTICS_FIELDS.forEach(({ key }) => {
    const raw = String(values[key] ?? '').trim();
    const saved = item[key] == null ? null : Number(item[key]);
    if (raw === '') {
      if (saved !== null) changed[key] = null;
      return;
    }
    if (!WHOLE_NUMBER.test(raw)) {
      problem = problem ?? measureProblem(key, raw);
      return;
    }
    const amount = Number(raw);
    if (amount !== saved) changed[key] = amount;
  });
  return { changed: Object.keys(changed).length > 0 ? changed : null, problem };
};

/**
 * The measures form once the server's item changed: a field the server moved
 * takes the server's value, the others keep what was typed and not saved.
 *
 * @param {Object<string, string>} before - The server's values the form last saw.
 * @param {Object<string, string>} after - The server's values now.
 * @param {Object<string, string>} typed - What the form shows.
 * @returns {Object<string, string>}
 */
export const mergeMeasureValues = (before, after, typed) =>
  Object.fromEntries(
    Object.keys(after).map((key) => [key, after[key] !== before[key] ? after[key] : typed[key] ?? after[key]])
  );

/**
 * What a box lent the item, said in the band: its measures, and the weight the
 * item now has, which is the suggestion (the box's tare and the products'
 * weight). The fields are already on screen; the point is that they did not
 * appear out of nowhere and can be corrected.
 *
 * @param {{sku: string}} supply
 * @param {string[]} filled - The fields the server filled.
 * @param {{weight_grams?: number}} [item] - The item after the write.
 */
export const boxFilledText = (supply, filled, item) => {
  const dims = filled.includes('length_cm');
  const weight = filled.includes('weight_grams') && item?.weight_grams != null ? formatQuantity(item.weight_grams) : null;
  if (dims && weight) return `Medidas de ${supply.sku} y peso sugerido (${weight} g); corrígelos si no corresponden.`;
  if (dims) return `Medidas de ${supply.sku}; corrígelas si no corresponden.`;
  if (weight) return `Peso sugerido: ${weight} g.`;
  return null;
};

/**
 * What the item should weigh by what it holds, and where that comes from:
 * the admin's note, with the floor's thousands.
 */
export const itemWeightSuggestionText = (item) => {
  const suggestion = item?.weight_suggestion;
  if (!(suggestion?.total > 0)) return null;
  const parts = [];
  if (suggestion.tare_grams > 0) parts.push(`${formatQuantity(suggestion.tare_grams)} g de caja`);
  if (suggestion.products_grams > 0) parts.push(`${formatQuantity(suggestion.products_grams)} g de productos`);
  return `Sugerido ${formatQuantity(suggestion.total)} g: ${parts.join(' + ')}`;
};

/**
 * Whether an item's weight is still the suggestion, or nothing: one weighed
 * and typed by hand is known whatever the catalogue says.
 */
const followsSuggestion = (item) =>
  item?.weight_grams == null || Number(item.weight_grams) === Number(item.weight_suggestion?.total ?? 0);

/**
 * The SKUs without weight in the catalogue of these items not weighed by
 * hand, once each: the server names one per line. Their weight is short.
 */
export const unweighedSkus = (items) => [
  ...new Set((items || []).filter(followsSuggestion).flatMap((item) => item?.weight_suggestion?.unweighed || [])),
];

/** «GOR-1», «GOR-1 y POL-1», «GOR-1, POL-1 y TAZ-1». */
const skuList = (skus) => (skus.length < 2 ? skus.join('') : `${skus.slice(0, -1).join(', ')} y ${skus[skus.length - 1]}`);

/**
 * What to do with an item whose weight leaves out products without weight in
 * the catalogue: weigh it. Gone once the item was weighed by hand.
 */
export const itemUnweighedNote = (item) => {
  const skus = unweighedSkus([item]);
  if (skus.length === 0) return null;
  return `${skuList(skus)} ${skus.length === 1 ? 'no tiene' : 'no tienen'} peso registrado: pesa el bulto.`;
};

/** How much of what was picked is already in an item. */
export const packingProgress = (order) => {
  const lines = order?.lines || [];
  const packed = lines.reduce((total, line) => total + Number(line.in_items || 0), 0);
  const picked = lines.reduce((total, line) => total + Number(line.picked || 0), 0);
  return { packed, picked };
};

/**
 * The order, without what only that one answer said. What it says of the
 * order as it stands — `gates`, `labels`, `next_order`, `queue_count` — stays.
 */
export const packingOrderOf = (payload) => {
  const { packed_now: packedNow, item_id: itemId, filled, replayed, ...order } = payload || {};
  return order;
};

/** The units of a line still outside the items. */
const unitsOutOf = (line) => {
  const out = line.to_pack ?? Number(line.picked || 0) - Number(line.in_items || 0);
  return Math.max(0, Number(out) || 0);
};

/**
 * What closes an order on the floor, worked out from its lines and items as
 * the server does: at least one item, every item measured and nothing picked
 * left outside them. For a server that does not send `gates` yet.
 *
 * @param {object} order
 * @returns {{has_items: boolean, unmeasured_items: number[], units_picked: number, units_in_items: number,
 *   units_out: number, ready: boolean}}
 */
export const derivePackingGates = (order) => {
  const lines = order?.lines || [];
  const items = order?.items || [];
  const unitsOut = lines.reduce((total, line) => total + unitsOutOf(line), 0);
  const unmeasured = items
    .filter((item) => !item.measured)
    .map((item) => item.item_index)
    .sort((a, b) => a - b);
  return {
    has_items: items.length > 0,
    unmeasured_items: unmeasured,
    units_picked: lines.reduce((total, line) => total + Number(line.picked || 0), 0),
    units_in_items: lines.reduce((total, line) => total + Number(line.in_items || 0), 0),
    units_out: unitsOut,
    ready: items.length > 0 && unmeasured.length === 0 && unitsOut === 0,
  };
};

/** The order's gates: the server's, or worked out from its lines and items. */
export const packingGatesOf = (order) => order?.gates ?? derivePackingGates(order);

/**
 * Whether a write's answer leaves the order packed by it: it packed it now,
 * or it is the replay of the write that did — a replay never says `packed_now`.
 *
 * @param {object} payload - The server's answer.
 */
export const packedByWrite = (payload) =>
  payload?.packed_now === true || (payload?.replayed === true && packingGatesOf(packingOrderOf(payload)).ready === true);

/** "2", "2 y 3", "1, 2 y 3". */
const indexList = (indexes) => {
  const sorted = [...indexes].sort((a, b) => a - b).map(String);
  return sorted.length < 2 ? sorted.join('') : `${sorted.slice(0, -1).join(', ')} y ${sorted[sorted.length - 1]}`;
};

/** «1 bulto», «2 bultos». */
const itemsCountText = (count) => `${count} ${Number(count) === 1 ? 'bulto' : 'bultos'}`;

/** What the measures gate asks for: «Mide el bulto 2», «Mide los bultos 2 y 3». */
export const measureMissingText = (indexes) =>
  indexes.length === 1 ? `Mide el bulto ${indexes[0]}` : `Mide los bultos ${indexList(indexes)}`;

/** What the units gate asks for: «Pon 2 un más en un bulto». */
export const unitsMissingText = (unitsOut) => `Pon ${formatQuantity(unitsOut)} un más en un bulto`;

/** «1 de 2 bultos medidos», «0 de 1 bulto medido». */
export const measuredItemsText = (measured, total) =>
  `${measured} de ${total} ${Number(total) === 1 ? 'bulto medido' : 'bultos medidos'}`;

const ADD_ITEM_TEXT = 'Agrega un bulto';

/**
 * The gates as the checklist shows them, each saying what it lacks. An order
 * without lines has no units to put in.
 *
 * @param {object} order
 * @param {object} gates - See `packingGatesOf`.
 * @returns {Array<{gate: 'items'|'measures'|'units', ok: boolean, label: string, missing: string|null}>}
 */
export const packingGateRows = (order, gates) => {
  const total = (order?.items || []).length;
  const unmeasured = gates.unmeasured_items || [];
  const measuresOk = gates.has_items && unmeasured.length === 0;
  const rows = [
    { gate: 'items', ok: gates.has_items, label: 'Al menos un bulto', missing: gates.has_items ? null : ADD_ITEM_TEXT },
    {
      gate: 'measures',
      ok: measuresOk,
      label: total > 0 ? `Bultos medidos · ${total - unmeasured.length} de ${total}` : 'Bultos medidos',
      missing: measuresOk ? null : unmeasured.length > 0 ? measureMissingText(unmeasured) : 'Mide cada bulto que agregues',
    },
  ];
  if ((order?.lines || []).length > 0) {
    const unitsOk = !(Number(gates.units_out) > 0);
    rows.push({
      gate: 'units',
      ok: unitsOk,
      label: `Todo lo pickeado en bultos · ${formatQuantity(gates.units_in_items)} de ${formatQuantity(gates.units_picked)} un`,
      missing: unitsOk ? null : unitsMissingText(gates.units_out),
    });
  }
  return rows;
};

/** «Faltan etiquetas para los bultos 2 y 3.», or for one. */
export const missingLabelsText = (indexes) =>
  indexes.length === 1
    ? `Falta la etiqueta del bulto ${indexes[0]}.`
    : `Faltan etiquetas para los bultos ${indexList(indexes)}.`;

const LABEL_TEXTS = {
  pending: 'El courier todavía no entrega la etiqueta.',
  missing: 'Esta orden no tiene etiqueta: el envío no se creó en el courier.',
};

/** «La etiqueta es de 1 bulto y la orden tiene 3 bultos: falta la etiqueta nueva.» */
const staleLabelsText = (labels) =>
  labels.labeled_items == null
    ? 'La etiqueta es de otros bultos: falta la etiqueta nueva.'
    : `La etiqueta es de ${itemsCountText(labels.labeled_items)} y la orden tiene ${itemsCountText(
        (labels.items || []).length
      )}: falta la etiqueta nueva.`;

/**
 * The labels of a packed order as the result shows them: a link per item
 * that has one, or the shipment's own, and what is wrong when they are not
 * all there. A label made for other items than the order has now (`stale`:
 * the floor added or took one out) is not offered: the shipment is created
 * again. The floor never asks the courier for them. `null` when the server
 * says nothing of labels.
 *
 * @param {object|null} labels
 * @returns {{status: string, tone: 'success'|'warning', text: string|null,
 *   links: Array<{key: string, href: string, itemIndex: number|null, text: string}>}|null}
 */
export const packingLabelsView = (labels) => {
  if (!labels?.status) return null;
  const perItem = (labels.items || [])
    .filter((item) => item.label_url)
    .sort((a, b) => a.item_index - b.item_index)
    .map((item) => ({
      key: `item-${item.item_id}`,
      href: item.label_url,
      itemIndex: item.item_index,
      text: `Etiqueta del bulto ${item.item_index}`,
    }));
  const shipment = labels.label_url
    ? [{ key: 'shipment', href: labels.label_url, itemIndex: null, text: 'Etiqueta del envío' }]
    : [];

  switch (labels.status) {
    case 'ready':
      return { status: 'ready', tone: 'success', text: null, links: perItem.length > 0 ? perItem : shipment };
    case 'mismatch':
      return {
        status: 'mismatch',
        tone: 'warning',
        text: missingLabelsText(labels.items_without_label || []),
        links: perItem,
      };
    case 'pending':
    case 'missing':
      return { status: labels.status, tone: 'success', text: LABEL_TEXTS[labels.status], links: [] };
    case 'stale':
      return { status: 'stale', tone: 'warning', text: staleLabelsText(labels), links: [] };
    default:
      return null;
  }
};

/**
 * The labels that go into one PDF: the links the result shows, in their order
 * — each item's, or the shipment's when no item has one —, as the server sent
 * them, with the item each is of (`null` for the shipment's). An item
 * labelled only in ZPL has no PDF to merge: `zplOnly` names it.
 *
 * @param {object|null} labels
 * @returns {{urls: string[], itemIndexes: Array<number|null>, zplOnly: number[]}}
 */
export const printableLabels = (labels) => {
  const links = packingLabelsView(labels)?.links ?? [];
  return {
    urls: links.map((link) => link.href),
    itemIndexes: links.map((link) => link.itemIndex),
    zplOnly: (labels?.items || [])
      .filter((item) => item.zpl_url && !item.label_url)
      .map((item) => item.item_index)
      .sort((a, b) => a - b),
  };
};

/** The print button: «Imprimir todas las etiquetas (3)»; with items left without one, «Imprimir las 2 que hay». */
export const printLabelsText = (labels) => {
  const count = printableLabels(labels).urls.length;
  return labels?.status === 'mismatch' ? `Imprimir las ${count} que hay` : `Imprimir todas las etiquetas (${count})`;
};

/**
 * What a labels PDF left out: labels it could not read, which their own links
 * still open, and items labelled only in ZPL. `null` when it has them all.
 *
 * @param {{unreadable?: number[], zplOnly?: number[]}} print - Item indexes.
 */
export const labelsLeftOutText = ({ unreadable = [], zplOnly = [] }) => {
  const parts = [];
  if (unreadable.length === 1) parts.push(`La etiqueta del bulto ${unreadable[0]} no se pudo leer. Ábrela desde su enlace.`);
  if (unreadable.length > 1) {
    parts.push(`Las etiquetas de los bultos ${indexList(unreadable)} no se pudieron leer. Ábrelas desde sus enlaces.`);
  }
  if (zplOnly.length === 1) parts.push(`La etiqueta del bulto ${zplOnly[0]} es ZPL y no va en el PDF.`);
  if (zplOnly.length > 1) parts.push(`Las etiquetas de los bultos ${indexList(zplOnly)} son ZPL y no van en el PDF.`);
  return parts.length > 0 ? parts.join(' ') : null;
};

/** «3 etiquetas», «2 de 3 etiquetas». */
const labelsCount = (included, total) => {
  const noun = total === 1 ? 'etiqueta' : 'etiquetas';
  return included === total ? `${total} ${noun}` : `${included} de ${total} ${noun}`;
};

/**
 * What the band says once the labels PDF is made. Not a `success`: nothing was
 * confirmed by the server. A popup blocker that kept it from opening is said
 * first: the screen offers the PDF as a link, which a real tap opens.
 *
 * @param {{opened: boolean, included: number, total: number, unreadable: number[], zplOnly: number[]}} print
 */
export const labelsPrintMessage = (print) => {
  if (!print.opened) return { tone: 'warning', message: 'El navegador no abrió el PDF: toca «Abrir el PDF de etiquetas».' };
  const made = `PDF con ${labelsCount(print.included, print.total)} listo para imprimir.`;
  const leftOut = labelsLeftOutText(print);
  return leftOut ? { tone: 'warning', message: `${made} ${leftOut}` } : { tone: 'step', message: made };
};

/** No label could be read: the first reason, and the way around it. */
export const labelsPrintFailedMessage = (error) => {
  const reason = asSentence(error?.message);
  const said = reason ? `: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}` : '.';
  return { tone: 'error', message: `No pudimos armar el PDF${said} Ábrelas una por una.` };
};

/*
 * Sending a packed order to its courier from the floor. The courier makes the
 * shipment and its labels in the background, and there is no socket on the
 * floor: the screen reads the order until they come.
 */

/** How often the screen reads an order sent to its courier, until its labels come. */
export const LABEL_POLL_MS = 3000;

/**
 * Whether an order still waits for its courier: an attempt to send it is out
 * and its labels are not all in — the server writes the tracking and then
 * each item's label before it says the attempt was sent, so `pending` and
 * `mismatch` are steps on the way —, or its shipment is being created again,
 * which the server says is over only once the new labels are in.
 */
export const awaitingLabel = (order) =>
  (order?.courier_send?.status === 'pending' && order?.labels?.status !== 'ready') || isRelabeling(order);

const courierOf = (order) => order?.courier?.label || null;

const sentenceStart = (text) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** Why an attempt failed, in the server's words; these, when it says none. */
const failedText = (attempt) => asSentence(attempt?.message) || 'El envío no se pudo crear en el courier.';

/** Whether creating the shipment again is at play: its label went stale, or the floor made it again. */
const relabelAtPlay = (order) => order?.labels?.status === 'stale' || Boolean(order?.relabel && order.relabel.state !== 'none');

export const COURIER_SEND_VIEW = {
  OFFER: 'offer',
  WAITING: 'waiting',
  TIMEOUT: 'timeout',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  SENT: 'sent',
};

/**
 * Why an order cannot go that no try can change: no button. For any other
 * reason the button stays, and the server's refusal says what is missing —
 * a destination not assigned is not the warehouse's to fix.
 */
const FINAL_BLOCKERS = ['meli', 'in_courier_hands', 'final_status', 'upsell', 'unconfirmed'];

/** Why an order cannot go that nobody has to fix: the rest ask for the supervisor, who works on the web. */
const INFORMATIVE_BLOCKERS = ['meli', 'in_courier_hands'];

/**
 * Where sending the packed order to its courier stands, as its block in the
 * result shows it; `null` when the labels speak for themselves.
 * - `waiting`: the screen reads the order until the labels come;
 * - `timeout`: an attempt still out that the screen no longer reads;
 * - `blocked`: why the order cannot go — also after an attempt that failed,
 *   such as one never confirmed, which the floor does not send again;
 * - `failed`: the server's words, and whether it can go again (`retryable`);
 * - `sent`: the shipment is there but its label is not, with the server's note;
 * - `offer`: «Enviar al courier».
 * `askSupervisor`: what is left to do is not the floor's. Once the floor
 * changed the items of a labeled order, creating the shipment again speaks
 * instead (`relabelView`).
 *
 * @param {object|null} order - As packing shows it: `labels`, `courier`, `courier_send`, `courier_blocker`.
 * @param {{labelWait?: {orderId: number}|null}} [courierSend] - The slice's.
 * @returns {{status: string, text: string|null, retryable?: boolean, askSupervisor?: boolean}|null}
 */
export const courierSendView = (order, courierSend) => {
  if (!order || relabelAtPlay(order)) return null;
  const attempt = order.courier_send || null;
  const courier = courierOf(order) || 'el courier';
  const labels = order.labels?.status;
  if (courierSend?.labelWait?.orderId === order.id) {
    return { status: COURIER_SEND_VIEW.WAITING, text: `Esperando la etiqueta de ${courier}…` };
  }
  // Still out and no longer read: its labels may be on their way, some of them in already.
  if (awaitingLabel(order)) {
    return { status: COURIER_SEND_VIEW.TIMEOUT, text: `${sentenceStart(courier)} todavía no entrega la etiqueta.` };
  }
  // Once the labels are in, they say it all.
  if (labels === 'ready' || labels === 'mismatch') return null;
  if (attempt?.status === 'sent') {
    return {
      status: COURIER_SEND_VIEW.SENT,
      text: asSentence(attempt.message || 'El envío se creó en el courier, pero su etiqueta no llegó: pídela desde la web'),
    };
  }
  const blocker = order.courier_blocker;
  if (FINAL_BLOCKERS.includes(blocker?.reason)) {
    return {
      status: COURIER_SEND_VIEW.BLOCKED,
      text: asSentence(blocker.message),
      askSupervisor: !INFORMATIVE_BLOCKERS.includes(blocker.reason),
    };
  }
  if (attempt?.status === 'failed' && attempt.retryable) {
    return { status: COURIER_SEND_VIEW.FAILED, text: failedText(attempt), retryable: true };
  }
  // The last refusal of this visit stays under the button: the band goes, and the button is there to try again.
  const refusal = courierSend?.refusal?.orderId === order.id ? courierSend.refusal.message : null;
  return { status: COURIER_SEND_VIEW.OFFER, text: refusal ? asSentence(refusal) : null };
};

/**
 * What the sheet asks before the order goes to its courier, which cannot be
 * taken back from the phone: its items, and their weight summed — what the
 * shipment is made with. A product without weight in the catalogue leaves
 * that weight short.
 *
 * @param {object} order
 */
export const courierSendSheet = (order) => {
  const items = order?.items || [];
  const courier = courierOf(order);
  return {
    title: `¿Enviar ${packingOrderName(order)} a ${courier || 'su courier'}?`,
    message: `Se crea el envío con ${shipmentItemsText(items)} y ${
      courier || 'el courier'
    } entrega la etiqueta. No se puede deshacer desde el teléfono.`,
    warning: unweighedShipmentWarning(items),
    confirmLabel: courier ? `Enviar a ${courier}` : 'Enviar al courier',
  };
};

/**
 * Whether the order on screen has no label yet and the floor can ask its
 * courier for one: leaving it so asks first (`leaveWithoutLabelSheet`), since
 * Despacho does not take it until it has one.
 *
 * @param {object|null} view - `courierSendView`.
 */
export const leavesWithoutLabel = (view) =>
  view?.status === COURIER_SEND_VIEW.OFFER || (view?.status === COURIER_SEND_VIEW.FAILED && Boolean(view.retryable));

/**
 * What the sheet asks when the result's primary would leave the order with no
 * label: it goes to its courier from the sheet, as «Enviar al courier» sends
 * it, or it is left for later, from Despacho or Consulta.
 *
 * @param {object} order
 */
export const leaveWithoutLabelSheet = (order) => {
  const send = courierSendSheet(order);
  return {
    title: '¿Seguir sin etiqueta?',
    message: `${packingOrderName(order)} todavía no tiene etiqueta: en Despacho no se va a poder subir. La puedes generar después desde Despacho o Consulta.`,
    sendText: `Si la envías: ${send.message.charAt(0).toLowerCase()}${send.message.slice(1)}`,
    warning: send.warning,
    confirmLabel: send.confirmLabel,
  };
};

/** «2 bultos (3.450 g)»: what a shipment is made with. */
const shipmentItemsText = (items) =>
  `${itemsCountText(items.length)} (${formatQuantity(
    items.reduce((total, item) => total + Number(item.weight_grams || 0), 0)
  )} g)`;

/** The products without weight in the catalogue of items nobody weighed, which leave the shipment's weight short. */
const unweighedShipmentWarning = (items) => {
  const unweighed = unweighedSkus(items);
  if (unweighed.length === 0) return null;
  return `${skuList(unweighed)} ${
    unweighed.length === 1 ? 'no tiene' : 'no tienen'
  } peso registrado y su bulto no se pesó: el peso del envío puede quedar bajo.`;
};

/**
 * What to say once the send went through: the order is with its courier, or
 * already was — or its earlier attempt with that key failed. It names the
 * order, so off screen it is said as is.
 *
 * @param {object} payload - The server's answer: the order, and `replayed`.
 */
export const courierSendMessage = (payload) => {
  const order = packingOrderOf(payload);
  const name = packingOrderName(order);
  const courier = courierOf(order) || 'el courier';
  const attempt = order.courier_send;
  if (attempt?.status === 'failed') return { tone: 'error', message: failedText(attempt) };
  if (attempt?.status === 'sent' || order.labels?.status === 'ready') {
    return { tone: 'success', message: `${name} ya estaba enviada a ${courier}.` };
  }
  return {
    tone: 'success',
    message: payload.replayed
      ? `${name} ya se estaba enviando a ${courier}: esperando la etiqueta.`
      : `${name} enviada a ${courier}: esperando la etiqueta.`,
  };
};

/**
 * What to say once the wait for an order's labels is over: they came — for
 * every item, or not —, the attempt failed, or the shipment is there without
 * its label. `null` while it still waits.
 *
 * @param {object} order
 */
export const courierWaitSignal = (order) => {
  if (!order || awaitingLabel(order)) return null;
  if (order.relabel && order.relabel.state !== 'none') return relabelWaitSignal(order);
  const name = packingOrderName(order);
  const { labels } = order;
  const attempt = order.courier_send;
  const count = printableLabels(labels).urls.length;
  if (labels?.status === 'ready') {
    return {
      tone: 'success',
      message: count === 1 ? `Llegó la etiqueta de ${name}.` : `Llegaron las etiquetas de ${name}.`,
    };
  }
  if (attempt?.status === 'failed') return { tone: 'error', message: failedText(attempt) };
  if (labels?.status === 'mismatch') {
    const came = count === 1 ? 'Llegó 1 etiqueta' : `Llegaron ${count} etiquetas`;
    return { tone: 'warning', message: `${came} de ${name}. ${missingLabelsText(labels.items_without_label || [])}` };
  }
  return {
    tone: 'warning',
    message: asSentence(attempt?.message) || `El envío de ${name} se creó en ${courierOf(order) || 'el courier'}; su etiqueta todavía no llega.`,
  };
};

/** The wait ran out with the attempt still out. */
export const courierTimeoutSignal = (order) => {
  const name = packingOrderName(order);
  const courier = sentenceStart(courierOf(order) || 'el courier');
  if (!isRelabeling(order)) return { tone: 'warning', message: `${courier} todavía no entrega la etiqueta de ${name}.` };
  return {
    tone: 'warning',
    message: labelsOnly(order.relabel)
      ? `Las etiquetas nuevas de ${name} todavía no están.`
      : `${courier} todavía no entrega la etiqueta nueva de ${name}.`,
  };
};

/*
 * Creating the shipment again once the floor changed the items of a labeled
 * order. No courier changes how many items a shipment has: the server creates
 * a new one with the items there are now — by itself, once packing is done —
 * and its label replaces the old one, which is not offered meanwhile. Where
 * the courier never gets the number of items, only the labels are made again
 * (`mode: 'labels'`). The screen waits for the new label as for a send.
 */

export const RELABEL_VIEW = {
  DUE: 'due',
  OFFER: 'offer',
  WAITING: 'waiting',
  TIMEOUT: 'timeout',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  DONE: 'done',
};

/** Whether only the labels are made again: the courier never gets the number of items. */
const labelsOnly = (relabel) => relabel?.mode === 'labels';

/**
 * Where creating the shipment again stands, as its block shows it; `null`
 * while the floor has not changed the items of the labeled order.
 * - `due`: it is made once packing is done, with the items there are then;
 * - `waiting`: the screen reads the order until the new label comes;
 * - `timeout`: one still out that the screen no longer reads;
 * - `offer`: packed and not made — the bulk send had the order —: the packer can;
 * - `failed`: the server's words, and whether it can go again (`retryable`);
 * - `blocked`: why it cannot be made from the floor, also after one never confirmed;
 * - `done`: the new label is in, to print in place of the old one; or the
 *   shipment is there without it (`labelFailed`), in the server's words.
 * `askSupervisor`: what is left to do is not the floor's; `action`: what the
 * button that makes it says.
 *
 * @param {object|null} order - As packing shows it: `relabel`, `courier`.
 * @param {object} [packing] - The `operatorPacking` slice: its label wait, and the last refusal.
 * @returns {{status: string, text: string, action?: string, retryable?: boolean, askSupervisor?: boolean,
 *   labelFailed?: boolean}|null}
 */
export const relabelView = (order, packing) => {
  const relabel = order?.relabel;
  if (!relabel) return null;
  const courier = courierOf(order) || 'el courier';
  const items = itemsCountText(relabel.items_count);
  const onlyLabels = labelsOnly(relabel);
  const action = onlyLabels ? 'Hacer las etiquetas de nuevo' : 'Volver a crear el envío';
  // The last refusal of this visit stays under the button: the band goes, and the button is there to try again.
  const refusal = packing?.relabel?.refusal?.orderId === order.id ? packing.relabel.refusal.message : null;
  switch (relabel.state) {
    case 'due':
      return { status: RELABEL_VIEW.DUE, text: `La etiqueta se vuelve a crear con ${items} al terminar de empacar.` };
    case 'pending':
      if (packing?.courierSend?.labelWait?.orderId === order.id) {
        return {
          status: RELABEL_VIEW.WAITING,
          text: onlyLabels ? `Haciendo las etiquetas nuevas con ${items}…` : `Creando el envío con ${items} en ${courier}…`,
        };
      }
      return {
        status: RELABEL_VIEW.TIMEOUT,
        text: onlyLabels ? 'Las etiquetas nuevas todavía no están.' : `${sentenceStart(courier)} todavía no entrega la etiqueta nueva.`,
      };
    case 'offer':
      return { status: RELABEL_VIEW.OFFER, text: asSentence(refusal || relabel.message), action };
    case 'failed':
      return {
        status: RELABEL_VIEW.FAILED,
        text: asSentence(refusal || relabel.message) || 'El envío nuevo no se pudo crear en el courier.',
        action: 'Reintentar',
        retryable: Boolean(relabel.retryable),
        askSupervisor: !relabel.retryable,
      };
    case 'blocked':
      return {
        status: RELABEL_VIEW.BLOCKED,
        text: asSentence(relabel.message),
        askSupervisor: !INFORMATIVE_BLOCKERS.includes(relabel.reason),
      };
    case 'done':
      if (relabel.label_failed) return { status: RELABEL_VIEW.DONE, text: asSentence(relabel.message), labelFailed: true };
      return {
        status: RELABEL_VIEW.DONE,
        text: onlyLabels
          ? `Etiquetas nuevas con ${items}: imprímelas y reemplaza las anteriores.`
          : `Etiqueta nueva con ${items}: imprímela y reemplaza la anterior.`,
      };
    default:
      return null;
  }
};

/**
 * What the sheet asks before the packer creates the shipment again, which
 * cannot be taken back from the phone: the items it goes with and their
 * weight summed. The old label stops being good.
 *
 * @param {object} order
 */
export const relabelSheet = (order) => {
  const items = order?.items || [];
  const name = packingOrderName(order);
  if (labelsOnly(order?.relabel)) {
    return {
      title: `¿Hacer de nuevo las etiquetas de ${name}?`,
      message: `Se hacen las etiquetas con ${shipmentItemsText(items)}. Las anteriores dejan de servir.`,
      warning: null,
      confirmLabel: 'Hacer las etiquetas',
    };
  }
  const courier = courierOf(order);
  return {
    title: `¿Volver a crear el envío de ${name}${courier ? ` en ${courier}` : ''}?`,
    message: `Se crea un envío nuevo con ${shipmentItemsText(items)} y ${
      courier || 'el courier'
    } entrega la etiqueta nueva; la anterior deja de servir. No se puede deshacer desde el teléfono.`,
    warning: unweighedShipmentWarning(items),
    confirmLabel: courier ? `Crear de nuevo en ${courier}` : 'Crear el envío de nuevo',
  };
};

/**
 * What to say once the packer's «Volver a crear el envío» went through: it is
 * being made, or already was — or its earlier attempt under that key failed.
 * It names the order, so off screen it is said as is.
 *
 * @param {object} payload - The server's answer: the order, and `replayed`.
 */
export const relabelRequestMessage = (payload) => {
  const order = packingOrderOf(payload);
  const name = packingOrderName(order);
  const courier = courierOf(order) || 'el courier';
  const { relabel } = order;
  if (relabel?.state === 'pending') {
    if (labelsOnly(relabel)) {
      return { tone: 'success', message: `Haciendo de nuevo las etiquetas de ${name}: esperando las nuevas.` };
    }
    return {
      tone: 'success',
      message: payload.replayed
        ? `El envío de ${name} ya se estaba creando de nuevo en ${courier}: esperando la etiqueta nueva.`
        : `Creando de nuevo el envío de ${name} en ${courier}: esperando la etiqueta nueva.`,
    };
  }
  if (relabel?.state === 'done') return { tone: 'success', message: `El envío de ${name} ya estaba creado de nuevo.` };
  const text = asSentence(relabel?.message);
  if (relabel?.state === 'failed') return { tone: 'error', message: text || 'El envío nuevo no se pudo crear en el courier.' };
  return { tone: 'warning', message: text || `El envío de ${name} no se volvió a crear.` };
};

/** How the wait for the new label ended: it came, to print in place of the old one, or the server's words why not. */
const relabelWaitSignal = (order) => {
  const name = packingOrderName(order);
  const { relabel } = order;
  const items = itemsCountText(relabel.items_count);
  if (relabel.state === 'done' && !relabel.label_failed) {
    return {
      tone: 'success',
      message: labelsOnly(relabel)
        ? `Están las etiquetas nuevas de ${name} con ${items}: imprímelas y reemplaza las anteriores.`
        : `Llegó la etiqueta nueva de ${name} con ${items}: imprímela y reemplaza la anterior.`,
    };
  }
  const text = asSentence(relabel.message);
  if (relabel.state === 'failed') return { tone: 'error', message: text || 'El envío nuevo no se pudo crear en el courier.' };
  return { tone: 'warning', message: text || `El envío de ${name} no se volvió a crear.` };
};

/**
 * Whether taking out or adding an item makes the order's label stale: the
 * courier labeled it, for the items it has now. Mercado Libre's never is.
 *
 * @param {object} order
 */
export const labelChangesWithItems = (order) =>
  ['ready', 'pending', 'mismatch'].includes(order?.labels?.status) && order?.courier_blocker?.reason !== 'meli';

const RELABEL_DUE_TEXT = 'La etiqueta se vuelve a crear al terminar de empacar.';

/**
 * What the sheet says before an item is taken out: its products go back to
 * be packed, and the label is made again when it was for the items there are.
 *
 * @param {object} order
 * @param {{relabels?: boolean}} [backend] - Whether the backend makes it again.
 */
export const itemRemovalText = (order, { relabels = false } = {}) => {
  const products = 'Sus productos vuelven a quedar por empacar.';
  return relabels && labelChangesWithItems(order) ? `${products} ${RELABEL_DUE_TEXT}` : products;
};

/**
 * Where the result's primary goes: back where the order was opened from to
 * make its label (`returnToOf`), the next order of the queue, or the queue.
 *
 * @param {{nextOrder: object|null, queueCount: number|null}} next - `selectPackingNext`.
 * @param {{to: string, label: string}|null} [returnTo]
 */
export const packingNextStep = ({ nextOrder, queueCount }, returnTo = null) => {
  if (returnTo) return { to: returnTo.to, label: returnTo.label, orderId: null };
  const queue = OPERATOR_FLOW_PATHS[OPERATOR_FLOW.PACKING];
  if (!nextOrder) return { to: queue, label: 'Volver a la cola', orderId: null };
  return {
    to: `${queue}/${nextOrder.id}`,
    label: queueCount != null ? `Siguiente orden (${queueCount})` : 'Siguiente orden',
    orderId: nextOrder.id,
  };
};

const selectPackingOrder = (state) => state.operatorPacking.order;

/** The gates of the order on screen, or `null` before it loads. */
export const selectPackingGates = createSelector([selectPackingOrder], (order) => (order ? packingGatesOf(order) : null));

/** The checklist of the order on screen; see `packingGateRows`. */
export const selectPackingGateRows = createSelector([selectPackingOrder, selectPackingGates], (order, gates) =>
  gates ? packingGateRows(order, gates) : []
);

/** The labels of the order on screen, as the server sent them, or `null`. */
export const selectPackingLabels = (state) => selectPackingOrder(state)?.labels ?? null;

/** Creating the shipment again of the order on screen, while the backend makes it: see `relabelView`. */
export const selectPackingRelabel = createSelector(
  [
    selectCourierRelabelReady,
    selectPackingOrder,
    (state) => state.operatorPacking.courierSend.labelWait,
    (state) => state.operatorPacking.relabel.refusal,
  ],
  (ready, order, labelWait, refusal) => (ready ? relabelView(order, { courierSend: { labelWait }, relabel: { refusal } }) : null)
);

/** Sending the order on screen to its courier, once the backend does it from the floor: see `courierSendView`. */
export const selectPackingCourierSend = createSelector(
  [selectCourierSendReady, selectPackingOrder, (state) => state.operatorPacking.courierSend],
  (ready, order, courierSend) => (ready ? courierSendView(order, courierSend) : null)
);

/** The labels PDF this visit made, while it holds the labels on screen; `null` otherwise. */
export const selectPackingLabelsPdf = (state) => {
  const print = state.operatorPacking.labelsPrint;
  return print.status === 'done' && print.url ? print : null;
};

/** What comes after the order on screen; the server sends it only once the order is ready. */
export const selectPackingNext = createSelector([selectPackingOrder], (order) => ({
  nextOrder: order?.next_order ?? null,
  queueCount: order?.queue_count ?? null,
}));

/** "bulto 2", by the number the order gives it. */
const itemName = (order, itemId) => {
  const item = itemOf(order, itemId);
  return item ? `bulto ${item.item_index}` : 'el bulto';
};

const packedSuffix = (payload) => (packedByWrite(payload) ? ' Orden empacada.' : '');

/** Once the floor changed the items of a labeled order, the band says the label is made again. */
const relabelSuffix = (order) => (order.relabel?.state === 'due' ? ` ${RELABEL_DUE_TEXT}` : '');

/** A scan in units: a box counts what it holds. */
const unitsOf = (arg) => (Number(arg.quantity) || 1) * (Number(arg.factor) || 1);

const NOT_TWICE = 'no se contó dos veces.';

/**
 * What to say once a packing write went through, by thunk. A replay is the
 * retry of an addition that had landed: the server did not make it twice.
 * Off its order's screen, nothing is said to scan.
 *
 * @param {string} kind - addItem | saveItem | addProduct | removeItem | addSupply | removeSupply
 * @param {object} payload - The server's answer.
 * @param {object} arg
 * @param {{onScreen?: boolean}} [where]
 */
export const packingMessage = (kind, payload, arg, { onScreen = true } = {}) => {
  const order = packingOrderOf(payload);

  switch (kind) {
    case 'addItem': {
      const item = itemOf(order, payload.item_id);
      const label = item ? `Bulto ${item.item_index}` : 'Bulto';
      if (payload.replayed) return { tone: 'success', message: `${label} ya estaba agregado: no se agregó otro.` };
      return onScreen
        ? { tone: 'step', message: `${label} agregado: escanea la caja.${relabelSuffix(order)}` }
        : { tone: 'success', message: `se agregó el ${label.toLowerCase()}.` };
    }
    case 'saveItem':
      return { tone: 'success', message: `Medidas del ${itemName(order, arg.itemId)} guardadas.${packedSuffix(payload)}` };
    case 'addProduct': {
      const what = arg.sku || arg.productCode;
      const where = itemName(order, arg.itemId);
      return payload.replayed
        ? { tone: 'success', message: `${what} ya estaba registrado en el ${where}: ${NOT_TWICE}${packedSuffix(payload)}` }
        : { tone: 'success', message: `+${formatQuantity(unitsOf(arg))} ${what} en el ${where}.${packedSuffix(payload)}` };
    }
    case 'removeItem':
      return { tone: 'success', message: `Bulto quitado.${relabelSuffix(order)}${packedSuffix(payload)}` };
    case 'addSupply': {
      const supply = (order.supply_options || []).find((item) => item.id === arg.supplyId) || { sku: arg.supplyCode };
      const where = itemName(order, arg.itemId);
      if (payload.replayed) {
        return { tone: 'success', message: `${supply.sku} ya estaba registrado en el ${where}: ${NOT_TWICE}${packedSuffix(payload)}` };
      }
      const filled = boxFilledText(supply, payload.filled || [], itemOf(order, arg.itemId));
      const what = `${isContainer(supply) ? supplyKindLabel(supply.kind) : 'Insumo'} ${supply.sku}`;
      return {
        tone: 'success',
        message: `${what} en el ${where}.${filled ? ` ${filled}` : ''}${packedSuffix(payload)}`,
      };
    }
    default:
      return { tone: 'success', message: 'Insumo quitado; el largo, el ancho y el alto se quedan.' };
  }
};

/**
 * What to say when a write found the order changed (412) or what it acted on
 * gone (404), once the order was loaded again: the server's reason, and
 * whether the reload worked (`reloaded: false` when it did not).
 *
 * @param {{message?: string, reloaded?: boolean}} error
 */
export const packingReloadedMessage = (error) => {
  const reason = asSentence(error?.message);
  const after = error?.reloaded === false ? 'No pudimos recargar la orden.' : 'Orden recargada.';
  return { tone: 'warning', message: reason ? `${reason} ${after}` : after };
};

/**
 * A removal sent again that found nothing (404): its first try had gone
 * through, so it is told as done, once the order was loaded again.
 *
 * @param {string} kind - removeItem | removeSupply
 * @param {{reloaded?: boolean}} error
 */
export const packingAlreadyRemovedMessage = (kind, error) => {
  const done = kind === 'removeItem' ? 'El bulto ya estaba quitado.' : 'El insumo ya estaba quitado.';
  return error?.reloaded === false
    ? { tone: 'warning', message: `${done} No pudimos recargar la orden.` }
    : { tone: 'success', message: done };
};
